import {
  Box,
  Typography,
  Card,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  FormControl,
  OutlinedInput,
  InputAdornment,
  TextField,
  Link,
  Divider,
  Button,
  Autocomplete,
  InputLabel,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Masonry from '@mui/lab/Masonry';

import Sidebar from '../../../components/common/sidebar/Sidebar';
import { AuthContext } from '../../../components/contextAPI/ContextAPI';
import CustomHeader from '../../../components/common/header/CustomHeader';
import OpaqueLoading from '../../../components/opaqueLoading/opaqueLoading';
import {
  creditDays,
  creditMarket,
} from '../../../components/master-data/MasterData';

const Container = styled.div`
  width: 100%;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const DistributorOverview = () => {
  const { auth } = useContext(AuthContext);

  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [stateObj, setStateObj] = useState({});
  const [currentUser, setCurrentUser] = useState({});
  const [inputData, setInputData] = useState({});
  const [zUsers, setZUsers] = useState([]);
  const [zUsersFiltered, setZUsersFiltered] = useState([]);
  const [selected, setSelected] = useState({});
  const [zLevelUsers, setZLevelUsers] = useState({});
  const [herokuData, setHerokuData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const currentUser = auth.user;
    setCurrentUser(currentUser);

    const dataObj = location.state || {};
    setStateObj(dataObj);

    let herokuSfid, herokuId;
    herokuSfid = dataObj?.heroku_id__c ?? dataObj?.sfid;
    herokuId = dataObj?.sfid;
    setHerokuData({
      herokuId,
      herokuSfid,
    });

    getDistributorMappingDetailsById(herokuId, herokuSfid);
    getTerritoryBasedUser();
  }, []);

  const getDistributorMappingDetailsById = async (herokuId, herokuSfid) => {
    try {
      setLoading(true);
      let result =
        await window.Platform.database.getDistributorMappingDetailsById({
          herokuId: herokuId,
          herokuSfid: herokuSfid,
        });

      const jsonData = result.data;

      setInputData((prev) => ({
        ...prev,
        Credit_Period_Specify_No_of_Days__c:
          jsonData.credit_period_specify_no_of_days__c,
        Creditibility_in_Market_for_Managers_O__c:
          jsonData.creditibility_in_market_for_managers_o__c,
        Credit_Limit_Specify_Amount__c: jsonData.credit_limit_specify_amount__c,
        // SAP org fields the VP enters at L3 approval (pre-filled if present).
        sales_org__c: jsonData.sales_org__c ?? '',
        distribution_channel__c: jsonData.distribution_channel__c ?? '',
        division__c: jsonData.division__c ?? '',
        customer_group__c: jsonData.customer_group__c ?? '',
        sales_group__c: jsonData.sales_group__c ?? '',
        plant__c: jsonData.plant__c ?? '',
        Comments__c:
          jsonData.Approval__c?.find((item) => item.rejection_level__c === 'L4')
            ?.comments__c || '',
      }));

      // Seed the Z1..Z8 pickers from whatever is already on the record. Z1 is
      // special: if it's empty it defaults to the logged-in user (spec §3.1),
      // and it stays read-only for the VP.
      const seededSelected = {};
      const seededZ = {};
      [1, 2, 3, 4, 5, 6, 7, 8].forEach((n) => {
        const sfid = jsonData[`z${n}_user__c`];
        const name = jsonData[`z${n}_user_name__c`];
        if (sfid) {
          seededSelected[`z${n}_user__c`] = { sfid, name__c: name ?? '' };
          seededZ[`z${n}_user__c`] = sfid;
        }
      });
      if (!seededZ.z1_user__c && auth?.user?.sfid) {
        seededSelected.z1_user__c = {
          sfid: auth.user.sfid,
          name__c: auth.user.name__c ?? auth.user.name ?? '',
        };
        seededZ.z1_user__c = auth.user.sfid;
      }
      setSelected(seededSelected);
      setZLevelUsers(seededZ);

      setData(result.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTerritoryBasedUser = async () => {
    try {
      setLoading(true);
      let result = await window.Platform.database.getTerritoryBasedUser();
      const clone = [...result.data];
      setZUsers(clone);
      const newClone = [...result.data];
      setZUsersFiltered(newClone);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (data) => {
    const errors = {};

    const fields = [
      'Credit_Period_Specify_No_of_Days__c',
      'Creditibility_in_Market_for_Managers_O__c',
      'Credit_Limit_Specify_Amount__c',
      'Comments__c',
    ];

    fields.forEach((field) => {
      if (!data[field]?.toString()?.trim()) {
        errors[field] = `${field
          .replace(/_/g, ' ')
          .replace(/__c$/, '')} is required`;
      }
    });

    return errors;
  };

  // A picklist field may hold either the selected option object ({name__c})
  // or the seeded string from the record — resolve to the plain string value.
  const pickVal = (v) => v?.name__c ?? v ?? '';

  const updateDistributorMappingDetailsById = async (isApproved) => {
    try {
      // Both approve and reject require the three credit fields + comment
      // (for VP and Finance alike). Store the result so unfilled fields render
      // with a red outline (error state).
      const result = validateForm(inputData);
      setErrors(result);
      if (Object.keys(result).length === 0) {
        Swal.fire({
          title: 'Are you sure?',
          text: "You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes',
        }).then(async (result) => {
          if (result.isConfirmed) {
            setLoading(true);

            const isVp = currentUser.profile__c === 'VP';

            // Common to every submit (approve or reject, any role).
            const basePayload = {
              Credit_Period_Specify_No_of_Days__c: pickVal(
                inputData.Credit_Period_Specify_No_of_Days__c
              ),
              Creditibility_in_Market_for_Managers_O__c: pickVal(
                inputData.Creditibility_in_Market_for_Managers_O__c
              ),
              Credit_Limit_Specify_Amount__c:
                inputData.Credit_Limit_Specify_Amount__c ?? '',
              Comments__c: inputData.Comments__c,
              isApproved: isApproved ? 'Yes' : 'No',
              sfid: herokuData.herokuId,
              herokuId: herokuData.herokuSfid,
            };

            // On a VP approve, also send the SAP org fields + Z1..Z8 (sfid and
            // name). On reject, those are intentionally omitted (spec §5).
            const payload =
              isVp && isApproved
                ? {
                    ...basePayload,
                    sales_org__c: inputData.sales_org__c ?? '',
                    distribution_channel__c:
                      inputData.distribution_channel__c ?? '',
                    division__c: inputData.division__c ?? '',
                    customer_group__c: inputData.customer_group__c ?? '',
                    sales_group__c: inputData.sales_group__c ?? '',
                    plant__c: inputData.plant__c ?? '',
                    z1_user__c: zLevelUsers.z1_user__c || '',
                    z2_user__c: zLevelUsers.z2_user__c || '',
                    z3_user__c: zLevelUsers.z3_user__c || '',
                    z4_user__c: zLevelUsers.z4_user__c || '',
                    z5_user__c: zLevelUsers.z5_user__c || '',
                    z6_user__c: zLevelUsers.z6_user__c || '',
                    z7_user__c: zLevelUsers.z7_user__c || '',
                    z8_user__c: zLevelUsers.z8_user__c || '',
                    z1_user_name__c: selected.z1_user__c?.name__c || '',
                    z2_user_name__c: selected.z2_user__c?.name__c || '',
                    z3_user_name__c: selected.z3_user__c?.name__c || '',
                    z4_user_name__c: selected.z4_user__c?.name__c || '',
                    z5_user_name__c: selected.z5_user__c?.name__c || '',
                    z6_user_name__c: selected.z6_user__c?.name__c || '',
                    z7_user_name__c: selected.z7_user__c?.name__c || '',
                    z8_user_name__c: selected.z8_user__c?.name__c || '',
                  }
                : basePayload;

            await window.Platform.database.updateDistributorMappingDetailsById(
              payload
            );

            navigate(-1);
            window.NotificationUtils.showSuccess(
              `Distributor mapping has been ${isApproved ? 'approved' : 'rejected'
              } successfully.`
            );
          }
        });
      } else {
        window.NotificationUtils.showError('Please fill all required fields.');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const disabled = data?.Approval__c?.some(
    (item) => item.rejection_level__c === 'L3'
  );

  // The same user may be assigned to multiple zonal levels. Z1 remains
  // read-only in the UI, but Z2-Z8 must all be able to use the full user list.
  const getOptions = () => zUsers;

  const handleZUserChange = (key, value) => {
    setSelected((prev) => ({ ...prev, [key]: value || null }));
    setZLevelUsers((prev) => ({ ...prev, [key]: value?.sfid || null }));
  };

  const commonProps = {
    fullWidth: true,
    autoComplete: 'off',
    getOptionLabel: (option) => {
      if (typeof option === 'string') return option;
      if (!option) return '';
      return option.name__c ?? '';
    },
    isOptionEqualToValue: (option, value) => option?.sfid === value?.sfid,
    filterOptions: (options, state) =>
      options.filter((o) =>
        [o.name__c, o.employee_id__c]
          .filter(Boolean)
          .some((value) =>
            value
              .toString()
              .toLowerCase()
              .includes((state.inputValue || '').toLowerCase())
          )
      ),
    renderOption: (props, option) => (
      <li {...props}>
        {option.name__c ?? ''}
        {option.employee_id__c ? ` (${option.employee_id__c})` : ''}
      </li>
    ),
    renderInput: (params) => <TextField {...params} label='' />,
  };

  const handleInputChange = (key, value) => {
    if (typeof value?.name__c === 'string') {
      setInputData((prev) => ({ ...prev, [key]: value }));
    } else {
      setInputData((prev) => ({ ...prev, [key]: value?.target?.value }));
    }

    // Clear this field's validation error once the user provides a value.
    setErrors((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

    const mappedObj = {
      Credit_Period_Specify_No_of_Days__c:
        'credit_period_specify_no_of_days__c',
      Creditibility_in_Market_for_Managers_O__c:
        'creditibility_in_market_for_managers_o__c',
      Credit_Limit_Specify_Amount__c: 'credit_limit_specify_amount__c',
    };

    setData((prev) => ({
      ...prev,
      [mappedObj[key]]:
        typeof value?.name__c === 'string'
          ? value?.name__c
          : value?.target?.value,
    }));
  };

  const valueReturn = (value) => {
    if (!value || value === 'undefined' || value === 'null') {
      return '-';
    } else {
      return value;
    }
  }

  // Read-only label/value row, matching the existing overview table styling.
  const infoRow = (label, value) => (
    <TableRow>
      <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
        {label}
      </TableCell>
      <TableCell
        align='right'
        sx={{ fontWeight: 400, borderBottom: 'none' }}
      >
        {valueReturn(value)}
      </TableCell>
    </TableRow>
  );

  // Latest approval row for a given rejection level (L2 = RM Head, L3 = VP
  // Head, L4 = Finance), from the Approval__c array in the detail response.
  const findApproval = (level) =>
    data?.Approval__c?.find((item) => item.rejection_level__c === level);

  // Approval levels shown as collapsible sections, in order.
  const APPROVAL_LEVELS = [
    { level: 'L2', title: 'RM Head Input' },
    { level: 'L3', title: 'VP Head Input' },
    { level: 'L4', title: 'Finance Input' },
  ];

  const hasProposerCreditDetails = Boolean(
    data?.proposed_payment_terms_in_days__c ||
      data?.credit_limit_specify_amount__c
  );

  // Zonal Users (Z1–Z8) assigned by the VP at L3 approval. Each level is shown
  // as "<name> - <code>" (e.g. "Amit Joshi - 323232"), read from the detail
  // response fields z{n}_user_name__c and z{n}_code__c. Code falls back to "NA".
  const zonalUserValue = (n) => {
    const name = data?.[`z${n}_user_name__c`];
    const code = data?.[`z${n}_code__c`];
    if (!name && !code) return '-';
    return `${name || '-'} - ${code || 'NA'}`;
  };

  const hasZonalUsers = [1, 2, 3, 4, 5, 6, 7, 8].some(
    (n) => data?.[`z${n}_user_name__c`] || data?.[`z${n}_code__c`]
  );

  // Read-only detail rows for one approval level. All levels show the credit
  // fields; L3 (VP) additionally shows the SAP org fields and Z1..Z8.
  const renderApprovalRows = (appr, level) => (
    <>
      {infoRow('Name', appr.name__c)}
      {infoRow('Employee ID', appr.employee_id__c)}
      {infoRow('Mobile Number', appr.mobile__c)}
      {infoRow(
        'Credit Period (No. of Days)',
        appr.credit_period_specify_no_of_days__c
      )}
      {infoRow('Credit Limit (Amount)', appr.credit_limit_specify_amount__c)}
      {infoRow(
        'Creditability in Market',
        appr.creditibility_in_market_for_managers_o__c
      )}
      {level === 'L3' ? (
        <>
          {infoRow('Sales Org.', appr.sales_org__c)}
          {infoRow('Distribution Channel', appr.distribution_channel__c)}
          {infoRow('Division', appr.division__c)}
          {infoRow('Customer Group', appr.customer_group__c)}
          {infoRow('Sales Group', appr.sales_group__c)}
          {infoRow('Plant', appr.plant__c)}
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <TableRow key={`z${n}`}>
              <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                {`Z${n}`}
              </TableCell>
              <TableCell
                align='right'
                sx={{ fontWeight: 400, borderBottom: 'none' }}
              >
                {valueReturn(data?.[`z${n}_user_name__c`])}
              </TableCell>
            </TableRow>
          ))}
        </>
      ) : null}
      {infoRow('Comments', appr.comments__c)}
      {infoRow('Date', appr.createddate)}
    </>
  );

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        backgroundColor: '#F6F6F6',
        minHeight: '100vh',
      }}
    >
      <Sidebar />
      <Container>
        {loading && <OpaqueLoading />}
        <CustomHeader name='distributor-overview' />
        <Box sx={{ px: 3 }}>
          <Box style={{ marginTop: '20px' }}>
            <Masonry columns={2} spacing={2}>
              <Card sx={{ padding: '20px', borderRadius: '10px', marginTop: 2 }}>
                <TableContainer>
                  <Table aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Name of firm:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.firm_name__c)}
                        </TableCell>
                      </TableRow>
                      {/* <TableRow>
                          <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                            Address:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.address__c)}
                          </TableCell>
                        </TableRow> */}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Billing Address Line 1:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.address__c ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Billing Address Line 2:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.address_2__c ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Billing Address Line 3:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.address_3__c ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          State:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.state_name__c)}

                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          District:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.district_name__c)}

                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Sub District:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.sub_district_name__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Village:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.village_name__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          City:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.city__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Pincode:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.pincode__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Telephone:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.telephone__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Mobile:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.mobile__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Is mobile linked with PAN and Aadhaar?
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.is_this_mobile_number_linked_with_aadhaa__c) === '-' ? 'No' : 'Yes'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Is this whatsapp number?
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.is_this_a_whatsapp_number__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Alternate mobiler number:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.alternate_mobile__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          E-mail:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.email__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Establishment year:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.establishment_year__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Nature of firms:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.nature_of_frims__c)}
                        </TableCell>
                      </TableRow>
                      {data?.nature_of_frims__c?.toLowerCase() === 'other' && <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Other Nature of firms:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.other_nature_of_firms__c)}
                        </TableCell>
                      </TableRow>}
                      {/* <TableRow>
                      <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                        Other Nature of firm:
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ fontWeight: 400, borderBottom: 'none' }}
                      >
                        {valueReturn(data?.other_nature_of_firms__c)}
                      </TableCell>
                    </TableRow> */}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, borderBottom: 'none' }}>
                          Type of business:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.type_of_business__c)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card>


              <Card
                sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Detail of proprietor/partner/directors/members
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Shop/Office:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.shop_office__c)}
                          </TableCell>
                        </TableRow> */}
                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Fertilizer Lic. No. Registration Date:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.fertilizer_lic_no_registration_date__c)}
                          </TableCell>
                        </TableRow> */}
                    </TableHead>
                  </Table>
                </TableContainer>
                {data.Proprietor_Details__c?.length > 0 && (
                  <Divider sx={{ width: '100%' }} />
                )}
                {data.Proprietor_Details__c?.length > 0 &&
                  data.Proprietor_Details__c.map((item, index) => {
                    return (
                      <>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            marginTop: 2,
                            marginBottom: 0,
                          }}
                        >
                          Owner {index + 1}
                        </Typography>
                        <TableContainer>
                          <Table
                            sx={{ minWidth: 350 }}
                            aria-label='simple table'
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  Full name of owners of business:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.name)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  Permanent residence address:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.permanent_residence_address__c)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  Present residence address:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.present_residence_address__c)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  Father's / Husband's name:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.father_s_husband_s_name__c)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  Mobile number:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.mobile_number__c)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  Email ID:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.email_id__c)}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                          </Table>
                        </TableContainer>
                        <Divider sx={{ width: '100%' }} />
                      </>
                    );
                  })}
              </Card>


              <Card
                sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Infrastructure
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Shop/Office:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.shop_office__c)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Godown facilities
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Area of Godown
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.area_of_godown_sq_feet__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Godown Facility
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.godown_facility__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          No. of employees
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.numberofemployees)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          No. of Vehicles
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.no_of_vehicles__c)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card>


              <Card
                sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  License & Registration
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          GSTIN Applied
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.gstin_applied__c) === '-' ? 'No' : 'Yes'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          GSTIN no.
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.gstin_no__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          GSTIN registration date.
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.gstin_registration_date__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Insecticides Lic. No.
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.inst_lic_no__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Insecticides Lic. No. registration date.
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.insecticides_lic_no_registration_date__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Insecticides Lic. No. valid upto.
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.insecticides_lic_no_valid_upto__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Fertiliser Lic No.
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.fert_lic_no__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Fertiliser Lic No. Registration Date.
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.fertilizer_lic_no_registration_date__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Fertiliser Lic No. Valid Upto.
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.fertilizer_lic_no_valid_upto__c)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card>

              <Card
                sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Security Deposit Details
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          DD/Cheque/Online Ref. No:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.dd_cheque_online_ref_no__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Date:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.date__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Bank Name:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.bank_name__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Amount:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.amount_inr__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Address:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.banker_addresss__c)}
                        </TableCell>
                      </TableRow>
                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Banker's Name:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.banker_s_name__c)}
                          </TableCell>
                        </TableRow> */}
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Cash Credit Limit:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.credit_limit__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Others:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.others_security_deposite_detai__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Total:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {Number(valueReturn(data?.others_security_deposite_detai__c) === '-' ? 0 : valueReturn(data?.others_security_deposite_detai__c)) + Number(valueReturn(data?.credit_limit__c) === '-' ? 0 : valueReturn(data?.credit_limit__c))}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card>


              <Card
                sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Detail of business
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Pesticides:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.pesticide_turnover__c ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Fertilizers:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.fertilizers_turnover__c ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Seeds:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.seeds_other_turnover__c ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Others:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.others_product_turnover__c ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Total:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {Number(valueReturn(data?.pesticide_turnover__c) === '-' ? 0 : valueReturn(data?.pesticide_turnover__c)) + Number(valueReturn(data?.fertilizers_turnover__c) === '-' ? 0 : valueReturn(data?.fertilizers_turnover__c)) + Number(valueReturn(data?.seeds_other_turnover__c) === '-' ? 0 : valueReturn(data?.seeds_other_turnover__c)) + Number(valueReturn(data?.others_product_turnover__c) === '-' ? 0 : valueReturn(data?.others_product_turnover__c))}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Is your firm income tax assesses:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.is_your_firm_income_tax_assessee__c) === '-' ? 'No' : 'Yes'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          PAN:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.pan_no__c)}
                        </TableCell>
                      </TableRow>

                      {/* <TableRow>
                        {data.Company_Details__c?.length > 0 && (
                          <Divider sx={{ width: '100%' }} />
                        )}
                      </TableRow>
                      {data.Company_Details__c?.length > 0 &&
                        data.Company_Details__c.map((item, index) => {
                          return (
                            <>
                              <TableRow>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    marginTop: 2,
                                    marginBottom: 0,
                                  }}
                                >
                                  Company {index + 1}
                                </Typography>
                              </TableRow>
                              
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  Name of company doing existing business:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.name_of_company_doing_existing_business__c)}
                                </TableCell>
                              </TableRow>
                              
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  No. of years of dealing:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.no_of_years_of_dealing__c)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 500,
                                    borderBottom: 'none',
                                  }}
                                >
                                  Annual Turnover:
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 400,
                                    borderBottom: 'none',
                                  }}
                                >
                                  {valueReturn(item?.annual_turnover__c)}
                                </TableCell>
                              </TableRow>
                           
                              <TableRow>
                                <Divider sx={{ width: '100%' }} />
                              </TableRow>
                            </>
                          );
                        })} */}

                      {data.Company_Details__c?.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ p: 0, borderBottom: "none" }}>
                            <Divider sx={{ width: "100%" }} />
                          </TableCell>
                        </TableRow>
                      )}

                      {data.Company_Details__c?.length > 0 &&
                        data.Company_Details__c.map((item, index) => (
                          <React.Fragment key={index}>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ borderBottom: "none", pt: 2 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                                  Company {index + 1}
                                </Typography>
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell sx={{ fontWeight: 500, borderBottom: "none" }}>
                                Name of company doing existing business:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 400, borderBottom: "none" }}>
                                {valueReturn(item?.name_of_company_doing_existing_business__c)}
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell sx={{ fontWeight: 500, borderBottom: "none" }}>
                                No. of years of dealing:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 400, borderBottom: "none" }}>
                                {valueReturn(item?.no_of_years_of_dealing__c)}
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell sx={{ fontWeight: 500, borderBottom: "none" }}>
                                Annual Turnover:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 400, borderBottom: "none" }}>
                                {valueReturn(item?.annual_turnover__c)}
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell colSpan={2} sx={{ p: 0, borderBottom: "none" }}>
                                <Divider sx={{ width: "100%" }} />
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Existing Companies Total Annual Turnover (INR) in lacs:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {
                            data.Company_Details__c?.length > 0 &&
                            data.Company_Details__c.reduce((acc, curr) => {
                              return (
                                acc + Number(valueReturn(curr?.annual_turnover__c) === '-' ? 0 : valueReturn(curr?.annual_turnover__c))
                              );
                            }, 0)
                          }
                        </TableCell>
                      </TableRow>

                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Cash / Credit:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.credit_limit__c)}
                          </TableCell>
                        </TableRow> */}
                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Total annual turnover:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.total_annual_turnover_in_lacs_iil__c)}
                          </TableCell>
                        </TableRow> */}
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Capital employed:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.capital_employed__c)}
                        </TableCell>
                      </TableRow>
                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Annual Income:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.annual_income__c)}
                          </TableCell>
                        </TableRow> */}
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Expected investment in IIL:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.expected_investment_in_iil__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Expected turnover in 1st year with IIL:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.expected_turnover_in_1st_year_with_iil__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Expected turnover in next year with IIL:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.expected_turnover_in_next_year_with_iil__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Proposed payment terms:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.proposed_payment_terms_in_days__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Credit limit required(in lakhs):
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.credit_limit_required_specify_amount_in__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Total no. of Retailers:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.total_no_of_retailers__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          District:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {

                            data.Total_Districts_Covered__c?.length > 0 &&
                            data.Total_Districts_Covered__c.map((item => item.districtname)).join(', ')
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          No of district covered:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {

                            data.Total_Districts_Covered__c?.length ?? 0
                          }
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Major crops:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Major_Crops__c?.length > 0 &&
                            data.Major_Crops__c.map((item) => item.crop_name__c).join(', ')}
                        </TableCell>
                      </TableRow>
                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            No. of village covered:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.no_of_village_covered__c)}
                          </TableCell>
                        </TableRow> */}

                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            No. of years dealing:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.no_of_credit_days__c)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Annual turnover:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.total_annual_turnover_in_lacs_iil__c)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            No. of dealers:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.rs_in_lacs__c)}
                          </TableCell>
                        </TableRow> */}
                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Total no. of Retailers:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {data?.total_no_of_retailers__c ?? '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            GST Number:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {data?.gstin_no__c &&
                            data.gstin_no__c.trim() !== '' &&
                            data.gstin_no__c.toLowerCase() !== 'null'
                              ? data.gstin_no__c
                              : '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            GST Registration Date:
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {data?.gstin_registration_date__c ?? '-'}
                          </TableCell>
                        </TableRow> */}
                      {/* {Array.isArray(data?.Total_Districts_Covered__c) &&
                          data.Total_Districts_Covered__c.length > 0 &&
                          data.Total_Districts_Covered__c.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell
                                sx={{ fontWeight: 500, borderBottom: 'none' }}
                              >
                                District {index + 1}:
                              </TableCell>
                              <TableCell
                                align='right'
                                sx={{ fontWeight: 400, borderBottom: 'none' }}
                              >
                                {item?.districtname || 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))} */}

                    </TableHead>
                  </Table>
                </TableContainer>

              </Card>



              {data.Product_Of_Interest__c?.length > 0 && (
                <Card
                  sx={{
                    marginTop: 2,
                    padding: '20px',
                    borderRadius: '10px',
                  }}
                >
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                  >
                    Product of Interest
                  </Typography>
                  {data.Product_Of_Interest__c?.length > 0 && (
                    <Divider sx={{ width: '100%' }} />
                  )}
                  {data.Product_Of_Interest__c?.length > 0 &&
                    data.Product_Of_Interest__c.map((item, index) => {
                      return (
                        <>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              marginTop: 2,
                              marginBottom: 0,

                            }}
                          >
                            Product {index + 1}
                          </Typography>
                          <TableContainer>
                            <Table
                              sx={{ minWidth: 350 }}
                              aria-label='simple table'
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      borderBottom: 'none',
                                    }}
                                  >
                                    Product name:
                                  </TableCell>
                                  <TableCell
                                    align='right'
                                    sx={{
                                      fontWeight: 400,
                                      borderBottom: 'none',
                                    }}
                                  >
                                    {valueReturn(item?.product_name__c)}
                                  </TableCell>
                                </TableRow>
                                {/* <TableRow>
                                    <TableCell
                                      sx={{
                                        fontWeight: 500,
                                        borderBottom: 'none',
                                      }}
                                    >
                                      Current fiscal year:
                                    </TableCell>
                                    <TableCell
                                      align='right'
                                      sx={{
                                        fontWeight: 400,
                                        borderBottom: 'none',
                                      }}
                                    >
                                      {item?.current_fiscal_year_quantity__c ??
                                        '-'}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        fontWeight: 500,
                                        borderBottom: 'none',
                                      }}
                                    >
                                      Next fiscal year:
                                    </TableCell>
                                    <TableCell
                                      align='right'
                                      sx={{
                                        fontWeight: 400,
                                        borderBottom: 'none',
                                      }}
                                    >
                                      {item?.next_fiscal_year__c ?? '-'}
                                    </TableCell>
                                  </TableRow> */}
                              </TableHead>
                            </Table>
                          </TableContainer>
                          <Divider sx={{ width: '100%' }} />
                        </>
                      );
                    })}
                </Card>
              )}



              {data.Sister_Company_Details__c?.length > 0 && (
                <Card
                  sx={{
                    marginTop: 2,
                    padding: '20px',
                    borderRadius: '10px',
                  }}
                >
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                  >
                    Sister Company (if any)
                  </Typography>
                  {data.Sister_Company_Details__c?.length > 0 && (
                    <Divider sx={{ width: '100%' }} />
                  )}
                  {data.Sister_Company_Details__c?.length > 0 &&
                    data.Sister_Company_Details__c.map((item, index) => {
                      return (
                        <>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              marginTop: 2,
                              marginBottom: 0,
                            }}
                          >
                            Company {index + 1}
                          </Typography>
                          <TableContainer>
                            <Table
                              sx={{ minWidth: 350 }}
                              aria-label='simple table'
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      borderBottom: 'none',
                                    }}
                                  >
                                    Sister company name:
                                  </TableCell>
                                  <TableCell
                                    align='right'
                                    sx={{
                                      fontWeight: 400,
                                      borderBottom: 'none',
                                    }}
                                  >
                                    {valueReturn(item?.name)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      borderBottom: 'none',
                                    }}
                                  >
                                    Sister company address:
                                  </TableCell>
                                  <TableCell
                                    align='right'
                                    sx={{
                                      fontWeight: 400,
                                      borderBottom: 'none',
                                    }}
                                  >
                                    {valueReturn(item?.sister_company_address__c)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 500,
                                      borderBottom: 'none',
                                    }}
                                  >
                                    Sister company turnover (in lacs):
                                  </TableCell>
                                  <TableCell
                                    align='right'
                                    sx={{
                                      fontWeight: 400,
                                      borderBottom: 'none',
                                    }}
                                  >
                                    {valueReturn(item?.sister_company_turnover__c)}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                            </Table>
                          </TableContainer>
                          <Divider sx={{ width: '100%' }} />
                        </>
                      );
                    })}
                </Card>
              )}


              <Card
                sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Credit facility given to dealers
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Rs. in lacs
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.rs_in_lacs__c ?? '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          No. of credit days
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.no_of_credit_days__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Name of transporter 1
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.name_of_transporter_1__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Name of transporter 2
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.name_of_transporter_2__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Name of transporter 3
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.name_of_transporter_3__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Destination of dispatch of material
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Material_Dispatch_Destination__c?.length > 0
                            ? data?.Material_Dispatch_Destination__c.map(
                              (item) => item.depot_name__c
                            )?.join(', ')
                            : valueReturn(data?.Material_Dispatch_Destination__c?.[0])}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Depot Distance
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.distance_from_depot__c)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Any other details / info about you
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.any_other_detail_info_about__c)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card>


              <Card
                sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Official Use
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      {/* <TableRow>
                          <TableCell
                            sx={{ fontWeight: 500, borderBottom: 'none' }}
                          >
                            Credibility of party
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: 400, borderBottom: 'none' }}
                          >
                            {valueReturn(data?.credibility_of_party__c)}
                          </TableCell>
                        </TableRow> */}
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Have you visited the party personally?
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.have_you_visited_the_party_personally__c)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card>


              {/* <Card
                sx={{
                  marginTop: 2,
                  marginBottom: 2,
                  padding: '20px',
                  borderRadius: '10px',
                }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Attachment
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          GST Registration Certificate:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c ===
                                'GST_Registration_Certificate_Image__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c ===
                                  'GST_Registration_Certificate_Image__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c ===
                                      'GST_Registration_Certificate_Image__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) =>
                                    o?.name__c ===
                                    'GST_Registration_Certificate_Image__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Distributor Request Letter on Customer Letter Head:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c ===
                                'Distributor_Request_Letter_Image__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c ===
                                  'Distributor_Request_Letter_Image__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c ===
                                      'Distributor_Request_Letter_Image__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) =>
                                    o?.name__c ===
                                    'Distributor_Request_Letter_Image__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Shop/Godown:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c === 'Shop_Godown_Image__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c === 'Shop_Godown_Image__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c === 'Shop_Godown_Image__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'Shop_Godown_Image__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Bank Statement:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c === 'Bank_Statement_Pdf__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c === 'Bank_Statement_Pdf__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c === 'Bank_Statement_Pdf__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) =>
                                    o?.name__c === 'Bank_Statement_Pdf__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          ITR:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'ITR_Pdf__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) => item.name__c === 'ITR_Pdf__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'ITR_Pdf__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'ITR_Pdf__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Income tax declaration form:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'ITR_Pdf__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) => item.name__c === 'ITR_Pdf__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                  key={index}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'ITR_Pdf__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'ITR_Pdf__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          PAN:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'ITR_Pdf__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) => item.name__c === 'ITR_Pdf__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'ITR_Pdf__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'ITR_Pdf__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Copy of security Deposit cheque:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'Security_Deposit__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c === 'Security_Deposit__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'Security_Deposit__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'Security_Deposit__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Pesticide License:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c === 'Pesticide_License_Image__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c ===
                                  'Pesticide_License_Image__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c ===
                                      'Pesticide_License_Image__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) =>
                                    o?.name__c ===
                                    'Pesticide_License_Image__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>






                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Other Documents:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'Other_document__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) => item.name__c === 'Other_document__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'Other_document__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'Other_document__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card> */}


              {/* {currentUser.profile__c === 'RM' &&
                  !data.Approval__c?.some(
                    (item) => item.rejection_level__c === 'L3'
                  ) && (
                    <Card
                      sx={{
                        marginTop: 2,
                        marginBottom: 2,
                        padding: '20px',
                        borderRadius: '10px',
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                      >
                        RM Head Input
                      </Typography>
                      <Box
                        component='form'
                        sx={{
                          margin: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '20px',
                        }}
                        noValidate
                        autoComplete='off'
                      >
                        <Autocomplete
                          value={
                            data?.credit_period_specify_no_of_days__c?.toString() ||
                            ''
                          }
                          onChange={(event, newValue) =>
                            handleInputChange(
                              'Credit_Period_Specify_No_of_Days__c',
                              newValue
                            )
                          }
                          options={creditDays}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if (!option) return '';
                            return option.name__c ?? '';
                          }}
                          isOptionEqualToValue={(option, value) =>
                            (option?.name__c ?? '') === (value?.name__c ?? '')
                          }
                          fullWidth
                          autoComplete='off'
                          
                          disabled={data?.Approval__c?.some(
                            (item) => item.rejection_level__c === 'L2'
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Credit Period (Specify No. of Days)'
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                },
                              }}
                              required
                              error={Boolean(
                                errors.Credit_Period_Specify_No_of_Days__c
                              )}
                            />
                          )}
                          filterOptions={(options, state) => {
                            return options?.filter((option) =>
                              option.name__c?.toLowerCase()
                            );
                          }}
                        />

                        <FormControl variant='outlined' fullWidth>
                          <OutlinedInput
                            endAdornment={
                              <InputAdornment position='end'>₹</InputAdornment>
                            }
                            aria-describedby='outlined-weight-helper-text'
                            inputProps={{
                              'aria-label': 'weight',
                            }}
                            disabled={data?.Approval__c?.some(
                              (item) => item.rejection_level__c === 'L2'
                            )}
                            value={data?.credit_limit_specify_amount__c ?? ''}
                          />
                        </FormControl>

                        <Autocomplete
                          value={
                            data?.creditibility_in_market_for_managers_o__c ||
                            ''
                          }
                          onChange={(event, newValue) =>
                            handleInputChange(
                              'Creditibility_in_Market_for_Managers_O__c',
                              newValue
                            )
                          }
                          options={creditMarket}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if (!option) return '';
                            return option.name__c ?? '';
                          }}
                          isOptionEqualToValue={(option, value) =>
                            (option?.name__c ?? '') === (value?.name__c ?? '')
                          }
                          fullWidth
                          autoComplete='off'
                        
                          disabled={data?.Approval__c?.some(
                            (item) => item.rejection_level__c === 'L2'
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Creditability in Market'
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                    
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                },
                              }}
                              required
                              error={Boolean(
                                errors.Creditibility_in_Market_for_Managers_O__c
                              )}
                            />
                          )}
                          filterOptions={(options, state) => {
                            return options.filter((option) =>
                              option.name__c?.toLowerCase()
                            );
                          }}
                        />

                        <TextField
                          label='Comments'
                          variant='outlined'
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={4}
                          InputLabelProps={{
                            shrink: data?.Approval__c?.find(
                              (item) => item.rejection_level__c === 'L2'
                            )?.comments__c,
                          }}
                          defaultValue={
                            data?.Approval__c?.find(
                              (item) => item.rejection_level__c === 'L2'
                            )?.comments__c
                          }
                          disabled={data?.Approval__c?.some(
                            (item) => item.rejection_level__c === 'L2'
                          )}
                        />
                      </Box>
                    </Card>
                  )}

                {currentUser.profile__c !== 'RM' &&
                  data?.Approval__c?.some(
                    (item) => item.rejection_level__c === 'L2'
                  ) && (
                    <Card
                      sx={{
                        marginTop: 2,
                        marginBottom: 2,
                        padding: '20px',
                        borderRadius: '10px',
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                      >
                        RM Head Input
                      </Typography>
                      <Box
                        component='form'
                        sx={{
                          margin: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '20px',
                        }}
                        noValidate
                        autoComplete='off'
                      >
                        <Autocomplete
                          value={
                            data?.credit_period_specify_no_of_days__c?.toString() ||
                            ''
                          }
                          onChange={(event, newValue) =>
                            handleInputChange(
                              'Credit_Period_Specify_No_of_Days__c',
                              newValue
                            )
                          }
                          options={creditDays}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if (!option) return '';
                            return option.name__c ?? '';
                          }}
                          isOptionEqualToValue={(option, value) =>
                            (option?.name__c ?? '') === (value?.name__c ?? '')
                          }
                          fullWidth
                          autoComplete='off'
                          
                          disabled={data?.Approval__c?.some(
                            (item) => item.rejection_level__c === 'L2'
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Credit Period (Specify No. of Days)'
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                },
                              }}
                              required
                              error={Boolean(
                                errors.Credit_Period_Specify_No_of_Days__c
                              )}
                            />
                          )}
                          filterOptions={(options, state) => {
                            return options?.filter((option) =>
                              option.name__c?.toLowerCase()
                            );
                          }}
                        />

                        <FormControl variant='outlined' fullWidth>
                          <OutlinedInput
                            endAdornment={
                              <InputAdornment position='end'>₹</InputAdornment>
                            }
                            aria-describedby='outlined-weight-helper-text'
                            inputProps={{
                              'aria-label': 'weight',
                            }}
                            disabled={data?.Approval__c?.some(
                              (item) => item.rejection_level__c === 'L2'
                            )}
                            value={data?.credit_limit_specify_amount__c ?? ''}
                          />
                        </FormControl>

                        <Autocomplete
                          value={
                            data?.creditibility_in_market_for_managers_o__c ||
                            ''
                          }
                          onChange={(event, newValue) =>
                            handleInputChange(
                              'Creditibility_in_Market_for_Managers_O__c',
                              newValue
                            )
                          }
                          options={creditMarket}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if (!option) return '';
                            return option.name__c ?? '';
                          }}
                          isOptionEqualToValue={(option, value) =>
                            (option?.name__c ?? '') === (value?.name__c ?? '')
                          }
                          fullWidth
                          autoComplete='off'
                          
                          disabled={data?.Approval__c?.some(
                            (item) => item.rejection_level__c === 'L2'
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Creditability in Market'
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                     
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                },
                              }}
                              required
                              error={Boolean(
                                errors.Creditibility_in_Market_for_Managers_O__c
                              )}
                            />
                          )}
                          filterOptions={(options, state) => {
                            return options.filter((option) =>
                              option.name__c?.toLowerCase()
                            );
                          }}
                        />

                        <TextField
                          label='Comments'
                          variant='outlined'
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={4}
                          InputLabelProps={{
                            shrink: data?.Approval__c?.find(
                              (item) => item.rejection_level__c === 'L2'
                            )?.comments__c,
                          }}
                          defaultValue={
                            data?.Approval__c?.find(
                              (item) => item.rejection_level__c === 'L2'
                            )?.comments__c
                          }
                          disabled={data?.Approval__c?.some(
                            (item) => item.rejection_level__c === 'L2'
                          )}
                        />
                      </Box>
                    </Card>
                  )} */}


              {currentUser.profile__c === 'VP' &&
                data?.Approval__c?.some(
                  (item) => item.rejection_level__c === 'L2'
                ) &&
                !data?.Approval__c?.some(
                  (item) => item.rejection_level__c === 'L3'
                ) && (
                  <Card
                    sx={{
                      marginTop: 2,
                      marginBottom: 2,
                      padding: '20px',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                    >
                      VP Head Input
                    </Typography>
                    <Box
                      component='form'
                      sx={{
                        margin: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                      }}
                      noValidate
                      autoComplete='off'
                    >
                      <Autocomplete
                        value={pickVal(
                          inputData.Credit_Period_Specify_No_of_Days__c
                        )}
                        onChange={(event, newValue) =>
                          handleInputChange(
                            'Credit_Period_Specify_No_of_Days__c',
                            newValue
                          )
                        }
                        options={creditDays}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          if (!option) return '';
                          return option.name__c ?? '';
                        }}
                        isOptionEqualToValue={(option, value) =>
                          (option?.name__c ?? '') ===
                          (value?.name__c ?? value ?? '')
                        }
                        fullWidth
                        autoComplete='off'
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Credit Period (Specify No. of Days)'
                            required
                            error={Boolean(
                              errors.Credit_Period_Specify_No_of_Days__c
                            )}
                          />
                        )}
                      />

                      <FormControl variant='outlined' fullWidth required>
                        <InputLabel htmlFor='vp-credit-limit'>
                          Credit Limit (Specify Amount)
                        </InputLabel>
                        <OutlinedInput
                          id='vp-credit-limit'
                          label='Credit Limit (Specify Amount)'
                          endAdornment={
                            <InputAdornment position='end'>₹</InputAdornment>
                          }
                          value={inputData.Credit_Limit_Specify_Amount__c ?? ''}
                          onChange={(e) =>
                            handleInputChange('Credit_Limit_Specify_Amount__c', e)
                          }
                          error={Boolean(errors.Credit_Limit_Specify_Amount__c)}
                        />
                      </FormControl>

                      <Autocomplete
                        value={pickVal(
                          inputData.Creditibility_in_Market_for_Managers_O__c
                        )}
                        onChange={(event, newValue) =>
                          handleInputChange(
                            'Creditibility_in_Market_for_Managers_O__c',
                            newValue
                          )
                        }
                        options={creditMarket}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          if (!option) return '';
                          return option.name__c ?? '';
                        }}
                        isOptionEqualToValue={(option, value) =>
                          (option?.name__c ?? '') ===
                          (value?.name__c ?? value ?? '')
                        }
                        fullWidth
                        autoComplete='off'
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Creditability in Market'
                            required
                            error={Boolean(
                              errors.Creditibility_in_Market_for_Managers_O__c
                            )}
                          />
                        )}
                      />

                      <TextField
                        label='Sales Org.'
                        variant='outlined'
                        fullWidth
                        value={inputData.sales_org__c ?? ''}
                        onChange={(e) =>
                          setInputData((prev) => ({
                            ...prev,
                            sales_org__c: e.target.value,
                          }))
                        }
                        error={Boolean(errors.sales_org__c)}
                      />
                      <TextField
                        label='Distribution Channel'
                        variant='outlined'
                        fullWidth
                        value={inputData.distribution_channel__c ?? ''}
                        onChange={(e) =>
                          setInputData((prev) => ({
                            ...prev,
                            distribution_channel__c: e.target.value,
                          }))
                        }
                        error={Boolean(errors.distribution_channel__c)}
                      />
                      <TextField
                        label='Division'
                        variant='outlined'
                        fullWidth
                        value={inputData.division__c ?? ''}
                        onChange={(e) =>
                          setInputData((prev) => ({
                            ...prev,
                            division__c: e.target.value,
                          }))
                        }
                        error={Boolean(errors.division__c)}
                      />
                      <TextField
                        label='Customer Group'
                        variant='outlined'
                        fullWidth
                        value={inputData.customer_group__c ?? ''}
                        onChange={(e) =>
                          setInputData((prev) => ({
                            ...prev,
                            customer_group__c: e.target.value,
                          }))
                        }
                        error={Boolean(errors.customer_group__c)}
                      />
                      <TextField
                        label='Sales Group'
                        variant='outlined'
                        fullWidth
                        value={inputData.sales_group__c ?? ''}
                        onChange={(e) =>
                          setInputData((prev) => ({
                            ...prev,
                            sales_group__c: e.target.value,
                          }))
                        }
                        error={Boolean(errors.sales_group__c)}
                      />
                      <TextField
                        label='Plant'
                        variant='outlined'
                        fullWidth
                        value={inputData.plant__c ?? ''}
                        onChange={(e) =>
                          setInputData((prev) => ({
                            ...prev,
                            plant__c: e.target.value,
                          }))
                        }
                        error={Boolean(errors.plant__c)}
                      />

                      <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 1 }}>
                        Zonal User Assignment (Z1 - Z8)
                      </Typography>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
                        const key = `z${n}_user__c`;
                        // Z1 stays read-only for the VP (spec §3.1).
                        const isZ1 = n === 1;
                        return (
                          <Autocomplete
                            key={key}
                            {...commonProps}
                            disabled={isZ1}
                            value={selected[key] || null}
                            onChange={(e, v) => handleZUserChange(key, v)}
                            options={getOptions()}
                            renderInput={(params) => (
                              <TextField {...params} label={`Z${n}`} />
                            )}
                          />
                        );
                      })}

                      <TextField
                        label='Comments'
                        required
                        variant='outlined'
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={inputData.Comments__c ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setInputData((prev) => ({
                            ...prev,
                            Comments__c: val,
                          }));
                          setErrors((prev) => {
                            if (!prev?.Comments__c) return prev;
                            const next = { ...prev };
                            delete next.Comments__c;
                            return next;
                          });
                        }}
                        error={Boolean(errors.Comments__c)}
                        helperText={
                          errors.Comments__c ? 'Comment is required' : ''
                        }
                      />

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          justifyContent: 'center',
                          mt: 2,
                        }}
                      >
                        <Button
                          variant='contained'
                          color='success'
                          size='large'
                          sx={{
                            px: 5,
                            fontWeight: 700,
                            boxShadow: 3,
                            textTransform: 'none',
                          }}
                          onClick={() =>
                            updateDistributorMappingDetailsById(true)
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          variant='contained'
                          color='error'
                          size='large'
                          sx={{
                            px: 5,
                            fontWeight: 700,
                            boxShadow: 3,
                            textTransform: 'none',
                          }}
                          onClick={() =>
                            updateDistributorMappingDetailsById(false)
                          }
                        >
                          Reject
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                )}

                {false && (
                  <></>
                  // <Card
                  //   sx={{
                  //     marginTop: 2,
                  //     marginBottom: 2,
                  //     padding: '20px',
                  //     borderRadius: '10px',
                  //   }}
                  // >
                  //   <Typography
                  //     sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                  //   >
                  //     VP Head Input
                  //   </Typography>
                  //   <Box
                  //     component='form'
                  //     sx={{
                  //       margin: 2,
                  //       display: 'flex',
                  //       flexDirection: 'column',
                  //       gap: '20px',
                  //     }}
                  //     noValidate
                  //     autoComplete='off'
                  //   >
                  //     <TextField
                  //       label='Sales Org.'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Sales Org.'
                  //       value={data?.sales_org__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       InputLabelProps={{
                  //         shrink: data?.sales_org__c,
                  //       }}
                  //     />

                  //     <TextField
                  //       label='Distribution Channel'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Distribution Channel'
                  //       value={data?.distribution_channel__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       InputLabelProps={{
                  //         shrink: data?.distribution_channel__c,
                  //       }}
                  //     />

                  //     <TextField
                  //       label='Division'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Division'
                  //       value={data?.division__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       InputLabelProps={{
                  //         shrink: data?.division__c,
                  //       }}
                  //     />

                  //     <TextField
                  //       label='Customer Group'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Customer Group'
                  //       value={data?.customer_group__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       InputLabelProps={{
                  //         shrink: data?.customer_group__c,
                  //       }}
                  //     />

                  //     <TextField
                  //       label='Sales Group'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Sales Group'
                  //       value={data?.sales_group__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       InputLabelProps={{
                  //         shrink: data?.sales_group__c,
                  //       }}
                  //     />

                  //     <TextField
                  //       label='Plant'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Plant'
                  //       value={data?.plant__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       InputLabelProps={{
                  //         shrink: data?.plant__c,
                  //       }}
                  //     />

                  //     <Autocomplete
                  //       {...commonProps}
                  //       value={selected.z1_user__c}
                  //       onChange={(e, v) =>
                  //         handleZUserChange('z1_user__c', v)
                  //       }
                  //       options={getOptions('z1_user__c')}
                  //       disabled={disabled}
                  //       renderInput={(params) => (
                  //         <TextField {...params} label='Z1' />
                  //       )}
                  //     />

                  //     <Autocomplete
                  //       {...commonProps}
                  //       value={selected.z2_user__c}
                  //       onChange={(e, v) =>
                  //         handleZUserChange('z2_user__c', v)
                  //       }
                  //       options={getOptions('z2_user__c')}
                  //       disabled={disabled}
                  //       renderInput={(params) => (
                  //         <TextField {...params} label='Z2' />
                  //       )}
                  //     />

                  //     <Autocomplete
                  //       {...commonProps}
                  //       value={selected.z3_user__c}
                  //       onChange={(e, v) =>
                  //         handleZUserChange('z3_user__c', v)
                  //       }
                  //       options={getOptions('z3_user__c')}
                  //       disabled={disabled}
                  //       renderInput={(params) => (
                  //         <TextField {...params} label='Z3' />
                  //       )}
                  //     />

                  //     <Autocomplete
                  //       {...commonProps}
                  //       value={selected.z4_user__c}
                  //       onChange={(e, v) =>
                  //         handleZUserChange('z4_user__c', v)
                  //       }
                  //       options={getOptions('z4_user__c')}
                  //       disabled={disabled}
                  //       renderInput={(params) => (
                  //         <TextField {...params} label='Z4' />
                  //       )}
                  //     />

                  //     <Autocomplete
                  //       {...commonProps}
                  //       value={selected.z5_user__c}
                  //       onChange={(e, v) =>
                  //         handleZUserChange('z5_user__c', v)
                  //       }
                  //       options={getOptions('z5_user__c')}
                  //       disabled={disabled}
                  //       renderInput={(params) => (
                  //         <TextField {...params} label='Z5' />
                  //       )}
                  //     />

                  //     <Autocomplete
                  //       {...commonProps}
                  //       value={selected.z6_user__c}
                  //       onChange={(e, v) =>
                  //         handleZUserChange('z6_user__c', v)
                  //       }
                  //       options={getOptions('z6_user__c')}
                  //       disabled={disabled}
                  //       renderInput={(params) => (
                  //         <TextField {...params} label='Z6' />
                  //       )}
                  //     />

                  //     <Autocomplete
                  //       {...commonProps}
                  //       value={selected.z7_user__c}
                  //       onChange={(e, v) =>
                  //         handleZUserChange('z7_user__c', v)
                  //       }
                  //       options={getOptions('z7_user__c')}
                  //       disabled={disabled}
                  //       renderInput={(params) => (
                  //         <TextField {...params} label='Z7' />
                  //       )}
                  //     />

                  //     <Autocomplete
                  //       {...commonProps}
                  //       value={selected.z8_user__c}
                  //       onChange={(e, v) =>
                  //         handleZUserChange('z8_user__c', v)
                  //       }
                  //       options={getOptions('z8_user__c')}
                  //       disabled={disabled}
                  //       renderInput={(params) => (
                  //         <TextField {...params} label='Z8' />
                  //       )}
                  //     />

                  //     <TextField
                  //       label='Comments'
                  //       variant='outlined'
                  //       fullWidth
                  //       multiline
                  //       minRows={2}
                  //       maxRows={4}
                  //       InputLabelProps={{
                  //         shrink: data?.Approval__c?.find(
                  //           (item) => item.rejection_level__c === 'L3'
                  //         )?.comments__c,
                  //       }}
                  //       defaultValue={
                  //         data?.Approval__c?.find(
                  //           (item) => item.rejection_level__c === 'L3'
                  //         )?.comments__c
                  //       }
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //     />
                  //   </Box>
                  // </Card>
                )}

              {currentUser.profile__c !== 'VP' &&
                data?.Approval__c?.some(
                  (item) => item.rejection_level__c === 'L3'
                ) && (
                  <></>
                  // <Card
                  //   sx={{
                  //     marginTop: 2,
                  //     marginBottom: 2,
                  //     padding: '20px',
                  //     borderRadius: '10px',
                  //   }}
                  // >
                  //   <Typography
                  //     sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                  //   >
                  //     VP Head Input
                  //   </Typography>
                  //   <Box
                  //     component='form'
                  //     sx={{
                  //       margin: 2,
                  //       display: 'flex',
                  //       flexDirection: 'column',
                  //       gap: '20px',
                  //     }}
                  //     noValidate
                  //     autoComplete='off'
                  //   >
                  //     <TextField
                  //       label='Sales Org.'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Sales Org.'
                  //       value={data?.sales_org__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       InputLabelProps={{
                  //         shrink: data?.sales_org__c,
                  //       }}
                  //     />

                  //     <TextField
                  //       label='Distribution Channel'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Distribution Channel'
                  //       value={data?.distribution_channel__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //     />

                  //     <TextField
                  //       label='Division'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Division'
                  //       value={data?.division__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //     />

                  //     <TextField
                  //       label='Customer Group'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Customer Group'
                  //       value={data?.customer_group__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //     />

                  //     <TextField
                  //       label='Sales Group'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Sales Group'
                  //       value={data?.sales_group__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //     />

                  //     <TextField
                  //       label='Plant'
                  //       variant='outlined'
                  //       fullWidth
                  //       placeholder='Plant'
                  //       value={data?.plant__c}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       InputLabelProps={{
                  //         shrink: data?.plant__c,
                  //       }}
                  //     />

                  //     <Autocomplete
                  //       value={data?.z1_user__c || null}
                  //       //   onChange={(event, newValue) => handleStateChange(newValue)}
                  //       options={['1', '2', '3', '4', '5']}
                  //       getOptionLabel={(option) => option}
                  //       isOptionEqualToValue={(option, value) =>
                  //         option === value
                  //       }
                  //       fullWidth
                  //       autoComplete='off'
                  //       //   onFocus={() => {
                  //       //     getStates();
                  //       //   }}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       //   loading={stateLoading}
                  //       renderInput={(params) => (
                  //         <TextField
                  //           {...params}
                  //           label='Z1'
                  //           //   onChange={(event) => debouncedSearchField(event, 'State')}
                  //           slotProps={{
                  //             input: {
                  //               ...params.InputProps,
                  //               endAdornment: (
                  //                 <React.Fragment>
                  //                   {/* { {stateLoading ? (
                  //               <CircularProgress color='inherit' size={20} />
                  //             ) : null}} */}
                  //                   {params.InputProps.endAdornment}
                  //                 </React.Fragment>
                  //               ),
                  //             },
                  //           }}
                  //           required
                  //         //   error={Boolean(errors.State__c)}
                  //         />
                  //       )}
                  //       filterOptions={(options, state) => {
                  //         return options.filter((option) =>
                  //           option.toLowerCase()
                  //         );
                  //       }}
                  //     />
                  //     <Autocomplete
                  //       value={data?.z2_user__c || null}
                  //       //   onChange={(event, newValue) => handleStateChange(newValue)}
                  //       options={['1', '2', '3', '4', '5']}
                  //       getOptionLabel={(option) => option}
                  //       isOptionEqualToValue={(option, value) =>
                  //         option === value
                  //       }
                  //       fullWidth
                  //       autoComplete='off'
                  //       //   onFocus={() => {
                  //       //     getStates();
                  //       //   }}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       //   loading={stateLoading}
                  //       renderInput={(params) => (
                  //         <TextField
                  //           {...params}
                  //           label='Z2'
                  //           //   onChange={(event) => debouncedSearchField(event, 'State')}
                  //           slotProps={{
                  //             input: {
                  //               ...params.InputProps,
                  //               endAdornment: (
                  //                 <React.Fragment>
                  //                   {/* { {stateLoading ? (
                  //               <CircularProgress color='inherit' size={20} />
                  //             ) : null}} */}
                  //                   {params.InputProps.endAdornment}
                  //                 </React.Fragment>
                  //               ),
                  //             },
                  //           }}
                  //           required
                  //         //   error={Boolean(errors.State__c)}
                  //         />
                  //       )}
                  //       filterOptions={(options, state) => {
                  //         return options.filter((option) =>
                  //           option.toLowerCase()
                  //         );
                  //       }}
                  //     />
                  //     <Autocomplete
                  //       value={data?.z3_user__c || null}
                  //       //   onChange={(event, newValue) => handleStateChange(newValue)}
                  //       options={['1', '2', '3', '4', '5']}
                  //       getOptionLabel={(option) => option}
                  //       isOptionEqualToValue={(option, value) =>
                  //         option === value
                  //       }
                  //       fullWidth
                  //       autoComplete='off'
                  //       //   onFocus={() => {
                  //       //     getStates();
                  //       //   }}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       //   loading={stateLoading}
                  //       renderInput={(params) => (
                  //         <TextField
                  //           {...params}
                  //           label='Z3'
                  //           //   onChange={(event) => debouncedSearchField(event, 'State')}
                  //           slotProps={{
                  //             input: {
                  //               ...params.InputProps,
                  //               endAdornment: (
                  //                 <React.Fragment>
                  //                   {/* { {stateLoading ? (
                  //               <CircularProgress color='inherit' size={20} />
                  //             ) : null}} */}
                  //                   {params.InputProps.endAdornment}
                  //                 </React.Fragment>
                  //               ),
                  //             },
                  //           }}
                  //           required
                  //         //   error={Boolean(errors.State__c)}
                  //         />
                  //       )}
                  //       filterOptions={(options, state) => {
                  //         return options.filter((option) =>
                  //           option.toLowerCase()
                  //         );
                  //       }}
                  //     />
                  //     <Autocomplete
                  //       value={data?.z4_user__c || null}
                  //       //   onChange={(event, newValue) => handleStateChange(newValue)}
                  //       options={['1', '2', '3', '4', '5']}
                  //       getOptionLabel={(option) => option}
                  //       isOptionEqualToValue={(option, value) =>
                  //         option === value
                  //       }
                  //       fullWidth
                  //       autoComplete='off'
                  //       //   onFocus={() => {
                  //       //     getStates();
                  //       //   }}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       //   loading={stateLoading}
                  //       renderInput={(params) => (
                  //         <TextField
                  //           {...params}
                  //           label='Z4'
                  //           //   onChange={(event) => debouncedSearchField(event, 'State')}
                  //           slotProps={{
                  //             input: {
                  //               ...params.InputProps,
                  //               endAdornment: (
                  //                 <React.Fragment>
                  //                   {/* { {stateLoading ? (
                  //               <CircularProgress color='inherit' size={20} />
                  //             ) : null}} */}
                  //                   {params.InputProps.endAdornment}
                  //                 </React.Fragment>
                  //               ),
                  //             },
                  //           }}
                  //           required
                  //         //   error={Boolean(errors.State__c)}
                  //         />
                  //       )}
                  //       filterOptions={(options, state) => {
                  //         return options.filter((option) =>
                  //           option.toLowerCase()
                  //         );
                  //       }}
                  //     />
                  //     <Autocomplete
                  //       value={data?.z5_user__c || null}
                  //       //   onChange={(event, newValue) => handleStateChange(newValue)}
                  //       options={['1', '2', '3', '4', '5']}
                  //       getOptionLabel={(option) => option}
                  //       isOptionEqualToValue={(option, value) =>
                  //         option === value
                  //       }
                  //       fullWidth
                  //       autoComplete='off'
                  //       //   onFocus={() => {
                  //       //     getStates();
                  //       //   }}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       //   loading={stateLoading}
                  //       renderInput={(params) => (
                  //         <TextField
                  //           {...params}
                  //           label='Z5'
                  //           //   onChange={(event) => debouncedSearchField(event, 'State')}
                  //           slotProps={{
                  //             input: {
                  //               ...params.InputProps,
                  //               endAdornment: (
                  //                 <React.Fragment>
                  //                   {/* { {stateLoading ? (
                  //               <CircularProgress color='inherit' size={20} />
                  //             ) : null}} */}
                  //                   {params.InputProps.endAdornment}
                  //                 </React.Fragment>
                  //               ),
                  //             },
                  //           }}
                  //           required
                  //         //   error={Boolean(errors.State__c)}
                  //         />
                  //       )}
                  //       filterOptions={(options, state) => {
                  //         return options.filter((option) =>
                  //           option.toLowerCase()
                  //         );
                  //       }}
                  //     />
                  //     <Autocomplete
                  //       value={data?.z6_user__c || null}
                  //       //   onChange={(event, newValue) => handleStateChange(newValue)}
                  //       options={['1', '2', '3', '4', '5']}
                  //       getOptionLabel={(option) => option}
                  //       isOptionEqualToValue={(option, value) =>
                  //         option === value
                  //       }
                  //       fullWidth
                  //       autoComplete='off'
                  //       //   onFocus={() => {
                  //       //     getStates();
                  //       //   }}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       //   loading={stateLoading}
                  //       renderInput={(params) => (
                  //         <TextField
                  //           {...params}
                  //           label='Z6'
                  //           //   onChange={(event) => debouncedSearchField(event, 'State')}
                  //           slotProps={{
                  //             input: {
                  //               ...params.InputProps,
                  //               endAdornment: (
                  //                 <React.Fragment>
                  //                   {/* { {stateLoading ? (
                  //               <CircularProgress color='inherit' size={20} />
                  //             ) : null}} */}
                  //                   {params.InputProps.endAdornment}
                  //                 </React.Fragment>
                  //               ),
                  //             },
                  //           }}
                  //           required
                  //         //   error={Boolean(errors.State__c)}
                  //         />
                  //       )}
                  //       filterOptions={(options, state) => {
                  //         return options.filter((option) =>
                  //           option.toLowerCase()
                  //         );
                  //       }}
                  //     />
                  //     <Autocomplete
                  //       value={data?.z7_user__c || null}
                  //       //   onChange={(event, newValue) => handleStateChange(newValue)}
                  //       options={['1', '2', '3', '4', '5']}
                  //       getOptionLabel={(option) => option}
                  //       isOptionEqualToValue={(option, value) =>
                  //         option === value
                  //       }
                  //       fullWidth
                  //       autoComplete='off'
                  //       //   onFocus={() => {
                  //       //     getStates();
                  //       //   }}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       //   loading={stateLoading}
                  //       renderInput={(params) => (
                  //         <TextField
                  //           {...params}
                  //           label='Z7'
                  //           //   onChange={(event) => debouncedSearchField(event, 'State')}
                  //           slotProps={{
                  //             input: {
                  //               ...params.InputProps,
                  //               endAdornment: (
                  //                 <React.Fragment>
                  //                   {/* { {stateLoading ? (
                  //               <CircularProgress color='inherit' size={20} />
                  //             ) : null}} */}
                  //                   {params.InputProps.endAdornment}
                  //                 </React.Fragment>
                  //               ),
                  //             },
                  //           }}
                  //           required
                  //         //   error={Boolean(errors.State__c)}
                  //         />
                  //       )}
                  //       filterOptions={(options, state) => {
                  //         return options.filter((option) =>
                  //           option.toLowerCase()
                  //         );
                  //       }}
                  //     />
                  //     <Autocomplete
                  //       value={data?.z8_user__c || null}
                  //       //   onChange={(event, newValue) => handleStateChange(newValue)}
                  //       options={['1', '2', '3', '4', '5']}
                  //       getOptionLabel={(option) => option}
                  //       isOptionEqualToValue={(option, value) =>
                  //         option === value
                  //       }
                  //       fullWidth
                  //       autoComplete='off'
                  //       //   onFocus={() => {
                  //       //     getStates();
                  //       //   }}
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //       //   loading={stateLoading}
                  //       renderInput={(params) => (
                  //         <TextField
                  //           {...params}
                  //           label='Z8'
                  //           //   onChange={(event) => debouncedSearchField(event, 'State')}
                  //           slotProps={{
                  //             input: {
                  //               ...params.InputProps,
                  //               endAdornment: (
                  //                 <React.Fragment>
                  //                   {/* { {stateLoading ? (
                  //               <CircularProgress color='inherit' size={20} />
                  //             ) : null}} */}
                  //                   {params.InputProps.endAdornment}
                  //                 </React.Fragment>
                  //               ),
                  //             },
                  //           }}
                  //           required
                  //         //   error={Boolean(errors.State__c)}
                  //         />
                  //       )}
                  //       filterOptions={(options, state) => {
                  //         return options.filter((option) =>
                  //           option.toLowerCase()
                  //         );
                  //       }}
                  //     />

                  //     <TextField
                  //       label='Comments'
                  //       variant='outlined'
                  //       fullWidth
                  //       multiline
                  //       minRows={2}
                  //       maxRows={4}
                  //       InputLabelProps={{
                  //         shrink: data?.Approval__c?.find(
                  //           (item) => item.rejection_level__c === 'L3'
                  //         )?.comments__c,
                  //       }}
                  //       defaultValue={
                  //         data?.Approval__c?.find(
                  //           (item) => item.rejection_level__c === 'L3'
                  //         )?.comments__c
                  //       }
                  //       disabled={data?.Approval__c?.some(
                  //         (item) => item.rejection_level__c === 'L3'
                  //       )}
                  //     />
                  //   </Box>
                  // </Card>
                )}



              {currentUser.profile__c === 'Finance' &&
                !data?.Approval__c?.some(
                  (item) =>
                    item.rejection_level__c === 'L4' &&
                    item.status__c === 'Approved'
                ) && (
                  <Card
                    sx={{
                      marginTop: 2,
                      marginBottom: 2,
                      padding: '20px',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                    >
                      Finance Head Input
                    </Typography>
                    <Box
                      component='form'
                      sx={{
                        margin: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                      }}
                      noValidate
                      autoComplete='off'
                    >
                      <Autocomplete
                        value={
                          data?.credit_period_specify_no_of_days__c?.toString() ||
                          ''
                        }
                        onChange={(event, newValue) =>
                          handleInputChange(
                            'Credit_Period_Specify_No_of_Days__c',
                            newValue
                          )
                        }
                        options={creditDays}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          if (!option) return '';
                          return option.name__c ?? '';
                        }}
                        isOptionEqualToValue={(option, value) =>
                          (option?.name__c ?? '') === (value?.name__c ?? '')
                        }
                        fullWidth
                        autoComplete='off'
                        // onFocus={() => {
                        //   getStates();
                        // }}
                        disabled={data?.Approval__c?.some(
                          (item) => item.rejection_level__c === 'L4'
                        )}
                        //   loading={stateLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Credit Period (Specify No. of Days)'
                            //   onChange={(event) => debouncedSearchField(event, 'State')}
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {/* { {stateLoading ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null} } */}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              },
                            }}
                            required
                            error={Boolean(
                              errors.Credit_Period_Specify_No_of_Days__c
                            )}
                          />
                        )}
                        filterOptions={(options, state) => {
                          return options?.filter((option) =>
                            option.name__c?.toLowerCase()
                          );
                        }}
                      />

                      <TextField
                        variant='outlined'
                        fullWidth
                        label='Credit Limit (Specify Amount)'
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>₹</InputAdornment>
                          ),
                        }}
                        InputLabelProps={{
                          shrink: !!data?.credit_limit_specify_amount__c,
                        }}
                        disabled={data?.Approval__c?.some(
                          (item) => item.rejection_level__c === 'L4'
                        )}
                        value={data?.credit_limit_specify_amount__c ?? ''}
                        onChange={(event) =>
                          handleInputChange(
                            'Credit_Limit_Specify_Amount__c',
                            event
                          )
                        }
                        required
                        error={Boolean(errors.Credit_Limit_Specify_Amount__c)}
                      />
                      <Autocomplete
                        value={
                          data?.creditibility_in_market_for_managers_o__c ||
                          ''
                        }
                        onChange={(event, newValue) =>
                          handleInputChange(
                            'Creditibility_in_Market_for_Managers_O__c',
                            newValue
                          )
                        }
                        options={creditMarket}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          if (!option) return '';
                          return option.name__c ?? '';
                        }}
                        isOptionEqualToValue={(option, value) =>
                          (option?.name__c ?? '') === (value?.name__c ?? '')
                        }
                        fullWidth
                        autoComplete='off'
                        //   onFocus={() => {
                        //     getStates();
                        //   }}
                        disabled={data?.Approval__c?.some(
                          (item) => item.rejection_level__c === 'L4'
                        )}
                        //   loading={stateLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Creditability in Market'
                            //   onChange={(event) => debouncedSearchField(event, 'State')}
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {/* { {stateLoading ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null} } */}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              },
                            }}
                            required
                            error={Boolean(
                              errors.Creditibility_in_Market_for_Managers_O__c
                            )}
                          />
                        )}
                        filterOptions={(options, state) => {
                          return options.filter((option) =>
                            option.name__c?.toLowerCase()
                          );
                        }}
                      />
                      <TextField
                        label={`Comment By ${data?.Approval__c?.at(-1)?.rejection_level__c
                          }`}
                        variant='outlined'
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        InputLabelProps={{
                          shrink: data?.Approval__c?.at(-1)?.comments__c,
                        }}
                        defaultValue={data?.Approval__c?.at(-1)?.comments__c}
                        disabled
                      />
                      {!data?.Approval__c?.some(
                        (item) => item.rejection_level__c === 'L4'
                      ) && (
                          <TextField
                            label='Comments'
                            variant='outlined'
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            InputLabelProps={{
                              shrink: data?.Approval__c?.find(
                                (item) => item.rejection_level__c === 'L4'
                              )?.comments__c,
                            }}
                            defaultValue={
                              data?.Approval__c?.find(
                                (item) => item.rejection_level__c === 'L4'
                              )?.comments__c
                            }
                            disabled={data?.Approval__c?.some(
                              (item) => item.rejection_level__c === 'L4'
                            )}
                            onChange={(event) =>
                              handleInputChange('Comments__c', event)
                            }
                            required
                            error={Boolean(errors.Comments__c)}
                          />
                        )}
                    </Box>
                  </Card>
                )}

              {/* Finance Head Input (read-only view for non-Finance users) is
                  commented out — it's replaced by the "Finance Input" section
                  inside the "Approval Details" accordion above. Disabled via a
                  `false &&` guard because the block contains nested JSX
                  comments, so it can't be wrapped in a single block comment. */}
              {false &&
                currentUser.profile__c !== 'Finance' &&
                data?.Approval__c?.some(
                  (item) => item.rejection_level__c === 'L4'
                ) && (
                  <Card
                    sx={{
                      marginTop: 2,
                      marginBottom: 2,
                      padding: '20px',
                      borderRadius: '10px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                    >
                      Finance Head Input
                    </Typography>
                    <Box
                      component='form'
                      sx={{
                        margin: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                      }}
                      noValidate
                      autoComplete='off'
                    >
                      <Autocomplete
                        value={
                          data?.credit_period_specify_no_of_days__c?.toString() ||
                          ''
                        }
                        //   onChange={(event, newValue) => handleStateChange(newValue)}
                        options={creditDays}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          if (!option) return '';
                          return option.name__c ?? '';
                        }}
                        isOptionEqualToValue={(option, value) =>
                          (option?.name__c ?? '') === (value?.name__c ?? '')
                        }
                        fullWidth
                        autoComplete='off'
                        // onFocus={() => {
                        //   getStates();
                        // }}
                        disabled={data?.Approval__c?.some(
                          (item) => item.rejection_level__c === 'L4'
                        )}
                        //   loading={stateLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Credit Period (Specify No. of Days)'
                            //   onChange={(event) => debouncedSearchField(event, 'State')}
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {/* { {stateLoading ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null}} */}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              },
                            }}
                            required
                          //   error={Boolean(errors.State__c)}
                          />
                        )}
                        filterOptions={(options, state) => {
                          return options?.filter((option) =>
                            option.name__c?.toLowerCase()
                          );
                        }}
                      />

                      <FormControl variant='outlined' fullWidth>
                        <OutlinedInput
                          endAdornment={
                            <InputAdornment position='end'>₹</InputAdornment>
                          }
                          aria-describedby='outlined-weight-helper-text'
                          inputProps={{
                            'aria-label': 'weight',
                          }}
                          disabled={data?.Approval__c?.some(
                            (item) => item.rejection_level__c === 'L4'
                          )}
                          value={data?.credit_limit_specify_amount__c ?? ''}
                        />
                      </FormControl>

                      <Autocomplete
                        value={
                          data?.creditibility_in_market_for_managers_o__c ||
                          ''
                        }
                        //   onChange={(event, newValue) => handleStateChange(newValue)}
                        options={creditMarket}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          if (!option) return '';
                          return option.name__c ?? '';
                        }}
                        isOptionEqualToValue={(option, value) =>
                          (option?.name__c ?? '') === (value?.name__c ?? '')
                        }
                        fullWidth
                        autoComplete='off'
                        //   onFocus={() => {
                        //     getStates();
                        //   }}
                        disabled={data?.Approval__c?.some(
                          (item) => item.rejection_level__c === 'L4'
                        )}
                        //   loading={stateLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Creditability in Market'
                            //   onChange={(event) => debouncedSearchField(event, 'State')}
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {/* { {stateLoading ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null}} */}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              },
                            }}
                            required
                          //   error={Boolean(errors.State__c)}
                          />
                        )}
                        filterOptions={(options, state) => {
                          return options.filter((option) =>
                            option.name__c?.toLowerCase()
                          );
                        }}
                      />

                      <TextField
                        label={`Comment By ${data?.Approval__c?.at(-1)?.rejection_level__c
                          }`}
                        variant='outlined'
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        InputLabelProps={{
                          shrink: data?.Approval__c?.at(-1)?.comments__c,
                        }}
                        defaultValue={data?.Approval__c?.at(-1)?.comments__c}
                        disabled={data?.Approval__c?.some(
                          (item) => item.rejection_level__c === 'L4'
                        )}
                      />

                      {!data?.Approval__c?.some(
                        (item) => item.rejection_level__c === 'L4'
                      ) && (
                          <TextField
                            label='Comments'
                            variant='outlined'
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            InputLabelProps={{
                              shrink: data?.Approval__c?.find(
                                (item) => item.rejection_level__c === 'L4'
                              )?.comments__c,
                            }}
                            defaultValue={
                              data?.Approval__c?.find(
                                (item) => item.rejection_level__c === 'L4'
                              )?.comments__c
                            }
                            disabled={data?.Approval__c?.some(
                              (item) => item.rejection_level__c === 'L4'
                            )}
                            onChange={(event, newValue) =>
                              handleInputChange('Comments__c', newValue)
                            }
                          />
                        )}
                    </Box>
                  </Card>
                )}



              <Card
                sx={{
                  marginTop: 2,
                  marginBottom: 2,
                  padding: '20px',
                  borderRadius: '10px',
                }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Attachment
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          GST Registration Certificate:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c ===
                                'GST_Registration_Certificate_Image__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c ===
                                  'GST_Registration_Certificate_Image__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c ===
                                      'GST_Registration_Certificate_Image__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) =>
                                    o?.name__c ===
                                    'GST_Registration_Certificate_Image__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Distributor Request Letter on Customer Letter Head:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c ===
                                'Distributor_Request_Letter_Image__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c ===
                                  'Distributor_Request_Letter_Image__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c ===
                                      'Distributor_Request_Letter_Image__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) =>
                                    o?.name__c ===
                                    'Distributor_Request_Letter_Image__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Shop/Godown:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c === 'Shop_Godown_Image__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c === 'Shop_Godown_Image__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c === 'Shop_Godown_Image__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'Shop_Godown_Image__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Bank Statement:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c === 'Bank_Statement_Pdf__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c === 'Bank_Statement_Pdf__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c === 'Bank_Statement_Pdf__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) =>
                                    o?.name__c === 'Bank_Statement_Pdf__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          ITR:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'ITR_Pdf__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) => item.name__c === 'ITR_Pdf__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'ITR_Pdf__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'ITR_Pdf__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Income tax declaration form:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'ITR_Pdf__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) => item.name__c === 'ITR_Pdf__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                  key={index}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'ITR_Pdf__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'ITR_Pdf__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          PAN:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'ITR_Pdf__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) => item.name__c === 'ITR_Pdf__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'ITR_Pdf__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'ITR_Pdf__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Copy of security Deposit cheque:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) => item.name__c === 'Security_Deposit__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c === 'Security_Deposit__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) => o.name__c === 'Security_Deposit__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o?.name__c === 'Security_Deposit__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Pesticide License:
                        </TableCell>

                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c === 'Pesticide_License_Image__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c ===
                                  'Pesticide_License_Image__c'
                              )?.map((o, index) => (
                                <Link
                                  key={index}
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c ===
                                      'Pesticide_License_Image__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) =>
                                    o?.name__c ===
                                    'Pesticide_License_Image__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Other Documents:
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {data?.Files__c?.length ? (
                            data.Files__c?.filter(
                              (item) =>
                                item.name__c === 'Other_document__c' || item.name__c === 'Other_document2__c' || item.name__c === 'Other_document3__c'
                            )?.length > 1 ? (
                              data.Files__c?.filter(
                                (item) =>
                                  item.name__c === 'Other_document__c' || item.name__c === 'Other_document2__c' || item.name__c === 'Other_document3__c'
                              )?.map((o, index) => (
                                <Link
                                  href={o?.picture__c}
                                  target='_blank'
                                  underline='none'
                                  style={{ marginLeft: '8px' }}
                                >
                                  View {index + 1}
                                </Link>
                              ))
                            ) : (
                              <Link
                                href={
                                  data.Files__c.find(
                                    (o) =>
                                      o.name__c === 'Other_document__c' || o.name__c === 'Other_document2__c' || o.name__c === 'Other_document3__c'
                                  )?.picture__c
                                }
                                target='_blank'
                                underline='none'
                              >
                                View
                              </Link>
                            )
                          ) : (
                            <Link
                              style={{
                                cursor: 'default',
                                opacity: 0.5,
                                backgroundColor: '#FFF',
                              }}
                              href={
                                data?.Files__c?.find(
                                  (o) => o.name__c === 'Other_document__c' || o.name__c === 'Other_document2__c' || o.name__c === 'Other_document3__c'
                                )?.picture__c
                              }
                              target='_blank'
                              underline='none'
                            >
                              View
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card>


              <Card
                sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
              >
                <Typography
                  sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                >
                  Proposer
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 350 }} aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Owner Name
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.owner_name)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Employee ID
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.owner_employee_id)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 500, borderBottom: 'none' }}
                        >
                          Mobile Number
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 400, borderBottom: 'none' }}
                        >
                          {valueReturn(data?.owner_mobile)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              </Card>


              {hasZonalUsers && (
                <Card
                  sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
                >
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                  >
                    Zonal Users (Z1–Z8)
                  </Typography>
                  <TableContainer>
                    <Table sx={{ minWidth: 350 }} aria-label='zonal users'>
                      <TableHead>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                          <TableRow key={`zonal-z${n}`}>
                            <TableCell
                              sx={{ fontWeight: 500, borderBottom: 'none' }}
                            >
                              {`Z${n} User`}
                            </TableCell>
                            <TableCell
                              align='right'
                              sx={{ fontWeight: 400, borderBottom: 'none' }}
                            >
                              {zonalUserValue(n)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableHead>
                    </Table>
                  </TableContainer>
                </Card>
              )}

              {(APPROVAL_LEVELS.some(({ level }) => findApproval(level)) ||
                hasProposerCreditDetails) && (
                <Card
                  sx={{ marginTop: 2, padding: '20px', borderRadius: '10px' }}
                >
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 700, marginY: 2 }}
                  >
                    Approval Details
                  </Typography>
                  {hasProposerCreditDetails && (
                    <Accordion key='proposer' disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                          Proposer Input
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer>
                          <Table
                            sx={{ minWidth: 350 }}
                            aria-label='proposer input details'
                          >
                            <TableHead>
                              {infoRow(
                                'Credit Period (No. of Days)',
                                data.proposed_payment_terms_in_days__c
                              )}
                              {infoRow(
                                'Credit Limit (in lakhs)',
                                data.proposed_limit_required_specify_amount__c
                              )}
                            </TableHead>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}
                  {APPROVAL_LEVELS.map(({ level, title }) => {
                    const appr = findApproval(level);
                    if (!appr) return null;
                    return (
                      <Accordion key={level} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {title}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          <TableContainer>
                            <Table
                              sx={{ minWidth: 350 }}
                              aria-label='simple table'
                            >
                              <TableHead>
                                {renderApprovalRows(appr, level)}
                              </TableHead>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Card>
              )}


            </Masonry>
          </Box>
        </Box>
        {!data.Approval__c?.some(
          (item) => item.rejection_level__c === 'L4' && item.status__c
        ) && (
            <FlexContainer
              style={{
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                margin: '15px 0 25px 0',
              }}
            >
              {currentUser.profile__c === 'Finance' && <Button
                variant='outlined'
                onClick={() => updateDistributorMappingDetailsById(true)}
              >
                Approve
              </Button>}
              {currentUser.profile__c === 'Finance' && <Button
                variant='outlined'
                color='error'
                onClick={() => updateDistributorMappingDetailsById(false)}
              >
                Reject
              </Button>}
            </FlexContainer>
          )}
      </Container>
    </div>
  );
};

export default DistributorOverview;
